import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { string, bool, func, object } from 'prop-types';

import { getEditTrackParams } from '../../../utils/requests/track_upload_requests';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import logError from '../../../utils/logError';
import { modalSignup } from '../../../utils/modal_funnel_util';

import ActionHeader from '../../shared/ActionHeader';
import ActivityUploadModal from '../../forms/ActivityUploadModal';
import DeleteModal from '../../DeleteRecording/DeleteModal/DeleteModal';
import DownloadRouteModal from '../../DownloadRouteModal';

const TrackDetailsCard = ({
  forcePhotoId,
  track,
  context,
  data,
  blockNonUserCreation,
  handleShareClick,
  messagingChannel,
  isFavorite,
  handleFavoriteClick,
  saveMapAsClone,
  handleTrackUpdate
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [activityModalEditProps, setActivityModalEditProps] = useState(null);
  const [editingEndpoint, setEditingEndpoint] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const openEditModal = activityModalProps => {
    setActivityModalEditProps(activityModalProps);
    setShowEditModal(true);
  };

  const getTrackUrl = () =>
    LanguageSupportUtil.wrapUrlSafe(`/explore${track.type === 'map' ? '/map/' : '/recording/'}${track.slug}`, context.languageRegionCode);

  const handleEditClick = () => {
    getEditTrackParams(`${getTrackUrl()}/edit`)
      .then(openEditModal)
      .catch(logError);
  };

  const handleDirectionsClick = () => {
    if (!context.currentUser) {
      blockNonUserCreation('explore-atmap-directions');
    }

    const { location } = data;
    window.open(`https://www.google.com/maps/dir/Current+Location/${location?.latitude},${location?.longitude}`);
  };

  const handleShare = () => {
    handleShareClick({
      type: data.presentationType,
      ugc: true,
      id: data.id,
      slug: data.slug,
      name: data.name,
      private: data.private,
      primaryKey: 'id'
    });
  };

  // admin option
  const handleEditEndpointClick = () => {
    if (editingEndpoint && !confirm(StringConstantsMixin.strings.TRACK_DETAIL.ENDPOINT_CONFIRMATION)) {
      return;
    }
    const newState = { editingEndpoint: !editingEndpoint };
    messagingChannel.publish('map.edit-endpoint-state-changed', newState);
    setEditingEndpoint(newState);
  };
  // admin option
  const handleCancelEditEndpointClick = () => {
    if (!editingEndpoint) {
      return;
    }
    messagingChannel.publish('map.edit-endpoint-state-changed', { editingEndpoint: false, canceled: true });
    setEditingEndpoint(false);
  };

  const toggleDownloadModal = () => {
    setShowDownloadModal(!showDownloadModal);
  };

  const openDownloadModal = () => {
    if (!context.currentUser) {
      const returnToUrl = window.location.pathname + window.location.search;
      modalSignup('explore-gpx-download', returnToUrl);
    }

    setShowDownloadModal(true);
  };

  return (
    <div>
      <ActionHeader
        data={data}
        user={data?.user}
        bannerPhotoId={forcePhotoId}
        context={context}
        isFavorite={isFavorite}
        handleFavoriteClick={handleFavoriteClick}
        handleDirectionsClick={handleDirectionsClick}
        handleShareClick={handleShare}
        saveMapAsClone={() => saveMapAsClone(data)}
        editingEndpoint={editingEndpoint}
        handleEditEndpointClick={handleEditEndpointClick}
        handleCancelEditEndpointClick={handleCancelEditEndpointClick}
        handleEditClick={handleEditClick}
        handleDeleteClick={toggleDeleteModal}
        openDownloadModal={openDownloadModal}
        type="track"
      />
      {context.currentUser &&
        showDownloadModal &&
        ReactDOM.createPortal(
          <DownloadRouteModal toggleDownloadModal={toggleDownloadModal} mapId={data.id} />,
          document.getElementById('modalPortal')
        )}
      {showEditModal &&
        ReactDOM.createPortal(
          <ActivityUploadModal
            {...activityModalEditProps}
            closeModal={() => setShowEditModal(false)}
            context={context}
            onTrackAdd={payload => {
              handleTrackUpdate(payload);
            }}
            disableDropzone
          />,
          document.getElementById('modalPortal')
        )}
      {showDeleteModal &&
        ReactDOM.createPortal(
          <DeleteModal languageRegionCode={context.languageRegionCode} trackId={data.ID} toggle={toggleDeleteModal} userSlug={data?.user?.slug} />,
          document.getElementById('modalPortal')
        )}
    </div>
  );
};

TrackDetailsCard.propTypes = {
  forcePhotoId: string,
  track: object,
  context: object,
  data: object,
  blockNonUserCreation: func,
  handleShareClick: func,
  messagingChannel: object,
  isFavorite: bool,
  handleFavoriteClick: func,
  saveMapAsClone: func,
  handleTrackUpdate: func
};

TrackDetailsCard.defaultProps = {
  forcePhotoId: '',
  track: {},
  context: {},
  data: {},
  blockNonUserCreation: () => {},
  handleShareClick: () => {},
  messagingChannel: {},
  isFavorite: false,
  handleFavoriteClick: () => {},
  saveMapAsClone: () => {},
  handleTrackUpdate: () => {}
};

export default TrackDetailsCard;
